import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CPInput } from "../CPInput";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { ACTION_BTN, DANGER_BTN } from "./estimateStyles";
import "./WorkTypeNameDialog.css";

export function WorkTypeNameDialog({
  title = "Work Type Name",
  fieldLabel = "Work Type Name",
  emptyMessage = "Work type name is required.",
  saveLabel = "Save",
  initialName = "",
  onSave,
  onClose,
}) {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useBodyScrollLock(true);

  useEffect(() => {
    setName(initialName);
    setError("");
  }, [initialName]);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, saving]);

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(emptyMessage);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const shouldClose = await onSave(trimmed);
      if (shouldClose !== false) {
        onClose?.();
      }
    } catch (saveError) {
      console.error("[WorkTypeNameDialog] Save failed:", saveError);
      setError(saveError.message || "Unable to save work type name.");
    } finally {
      setSaving(false);
    }
  }

  function handleOverlayClick(event) {
    if (event.target === event.currentTarget && !saving) {
      onClose?.();
    }
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="wt-name-dialog-overlay" onClick={handleOverlayClick}>
      <div
        className="wt-name-dialog-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="work-type-name-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div id="work-type-name-title" className="wt-name-dialog-title">
          {title}
        </div>

        <CPInput
          label={fieldLabel}
          value={name}
          onChange={setName}
          required
          inputRef={inputRef}
        />

        {error && <div className="wt-name-dialog-error">{error}</div>}

        <div className="wt-name-dialog-actions">
          <button
            type="button"
            style={ACTION_BTN}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : saveLabel}
          </button>
          <button
            type="button"
            style={DANGER_BTN}
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
